/**
 * Page-specific legal content registry.
 *
 * Only pages with an entry here render real sections; any legal page
 * without an entry keeps the shared "pending final legal review" fallback
 * in LegalPage.tsx. Company identification data stays in company.ts and is
 * rendered separately — it is not duplicated here.
 *
 * Nothing in this file invents processors, analytics tools, cookie
 * inventories, retention periods, certifications or audits.
 */

import type { RouteKey } from "@/lib/routes";

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

export interface LegalPageContent {
  sections: LegalSection[];
  /** Optional visible note (e.g. counsel-review advisory). */
  note?: string;
}

// ─── LEGAL NOTICE ──────────────────────────────────────────────────
const NOTICE_EN: LegalPageContent = {
  sections: [
    {
      heading: "Site publisher",
      paragraphs: [
        "This website (bnmgroup.it) is published by BNM S.R.L., whose company details are set out in the identification panel above. BNM S.R.L. is a company incorporated under Italian law, with its registered office in Castelfranco di Sotto (PI), Italy.",
      ],
    },
    {
      heading: "Intellectual property",
      paragraphs: [
        "Unless otherwise stated, the content of this website — including text, layout, graphics, logos and images — belongs to BNM S.R.L. or is used with permission. It may not be reproduced, distributed or used for commercial purposes without prior written consent, except where permitted by applicable law.",
      ],
    },
    {
      heading: "Content and liability",
      paragraphs: [
        "The information on this website is provided for general business information only and does not constitute a binding offer, professional advice or a solicitation. BNM S.R.L. makes reasonable efforts to keep the information accurate and up to date, but does not warrant that it is complete or error-free and accepts no liability for decisions taken on the basis of it.",
      ],
    },
    {
      heading: "External links",
      paragraphs: [
        "This website may contain links to third-party websites. BNM S.R.L. does not control, and is not responsible for, the content or practices of those websites.",
      ],
    },
    {
      heading: "Governing law",
      paragraphs: [
        "This website and this legal notice are governed by Italian law. Any dispute relating to the use of the website is subject to the jurisdiction of the competent court of the place where BNM S.R.L. has its registered office, without prejudice to any mandatory protections that may apply.",
      ],
    },
    {
      heading: "Contact",
      paragraphs: [
        "For any communication regarding this website, BNM S.R.L. can be reached at info@bnmgroup.it or by certified email (PEC) at bnmsrl@arubapec.it.",
      ],
    },
  ],
};

const NOTICE_IT: LegalPageContent = {
  sections: [
    {
      heading: "Titolare del sito",
      paragraphs: [
        "Il presente sito (bnmgroup.it) è pubblicato da BNM S.R.L., i cui dati societari sono riportati nel pannello di identificazione qui sopra. BNM S.R.L. è una società costituita secondo il diritto italiano, con sede legale a Castelfranco di Sotto (PI), Italia.",
      ],
    },
    {
      heading: "Proprietà intellettuale",
      paragraphs: [
        "Salvo diversa indicazione, i contenuti di questo sito — inclusi testi, impaginazione, grafica, loghi e immagini — appartengono a BNM S.R.L. o sono utilizzati su autorizzazione. Non possono essere riprodotti, distribuiti o utilizzati per finalità commerciali senza previo consenso scritto, salvo quanto consentito dalla legge applicabile.",
      ],
    },
    {
      heading: "Contenuti e responsabilità",
      paragraphs: [
        "Le informazioni presenti su questo sito hanno finalità di informazione commerciale generale e non costituiscono un'offerta vincolante, una consulenza professionale o una sollecitazione. BNM S.R.L. si adopera per mantenere le informazioni accurate e aggiornate, ma non ne garantisce la completezza o l'assenza di errori e non assume responsabilità per decisioni assunte sulla base di esse.",
      ],
    },
    {
      heading: "Link esterni",
      paragraphs: [
        "Questo sito può contenere link a siti di terzi. BNM S.R.L. non controlla, e non è responsabile per, i contenuti o le pratiche di tali siti.",
      ],
    },
    {
      heading: "Legge applicabile",
      paragraphs: [
        "Il presente sito e le presenti note legali sono regolati dalla legge italiana. Ogni controversia relativa all'uso del sito è soggetta alla giurisdizione del foro competente del luogo in cui BNM S.R.L. ha la propria sede legale, fatte salve eventuali tutele inderogabili applicabili.",
      ],
    },
    {
      heading: "Contatti",
      paragraphs: [
        "Per qualsiasi comunicazione relativa a questo sito, BNM S.R.L. è raggiungibile all'indirizzo info@bnmgroup.it o tramite posta elettronica certificata (PEC) all'indirizzo bnmsrl@arubapec.it.",
      ],
    },
  ],
};

// ─── ACCESSIBILITY STATEMENT ───────────────────────────────────────
const ACCESS_EN: LegalPageContent = {
  sections: [
    {
      heading: "Our commitment",
      paragraphs: [
        "BNM S.R.L. is committed to making this website usable by as many people as possible. As a design reference, we aim to follow recognised good practice for web accessibility, including the Web Content Accessibility Guidelines (WCAG) 2.1 at level AA.",
      ],
    },
    {
      heading: "Measures taken",
      paragraphs: [
        "The website is built with semantic HTML, keyboard-navigable interface elements, text alternatives for meaningful images, and colour combinations chosen for readable contrast. Accessibility is considered as part of ongoing design and development.",
      ],
    },
    {
      heading: "Known limitations",
      paragraphs: [
        "This is a statement of intent, not a formal conformance certification, and no independent accessibility audit is claimed. Some content may not yet fully meet every success criterion. We continue to review and improve the website over time.",
      ],
    },
    {
      heading: "Feedback",
      paragraphs: [
        "If you encounter an accessibility barrier on this website, please contact us at info@bnmgroup.it. Where possible, describe the page and the difficulty so that we can address it.",
      ],
    },
  ],
};

const ACCESS_IT: LegalPageContent = {
  sections: [
    {
      heading: "Il nostro impegno",
      paragraphs: [
        "BNM S.R.L. si impegna a rendere questo sito utilizzabile dal maggior numero possibile di persone. Come riferimento progettuale, cerchiamo di seguire le buone pratiche riconosciute in materia di accessibilità web, incluse le Web Content Accessibility Guidelines (WCAG) 2.1 al livello AA.",
      ],
    },
    {
      heading: "Misure adottate",
      paragraphs: [
        "Il sito è realizzato con HTML semantico, elementi dell'interfaccia navigabili da tastiera, alternative testuali per le immagini significative e combinazioni di colore scelte per un contrasto leggibile. L'accessibilità è considerata nell'ambito della progettazione e dello sviluppo continui.",
      ],
    },
    {
      heading: "Limiti noti",
      paragraphs: [
        "La presente è una dichiarazione di intenti, non una certificazione formale di conformità, e non si dichiara alcun audit di accessibilità indipendente. Alcuni contenuti potrebbero non soddisfare ancora pienamente ogni criterio di successo. Continuiamo a rivedere e migliorare il sito nel tempo.",
      ],
    },
    {
      heading: "Segnalazioni",
      paragraphs: [
        "Se riscontri una barriera di accessibilità su questo sito, contattaci all'indirizzo info@bnmgroup.it. Ove possibile, descrivi la pagina e la difficoltà riscontrata affinché possiamo intervenire.",
      ],
    },
  ],
};

// ─── TERMS OF USE ──────────────────────────────────────────────────
const TERMS_EN: LegalPageContent = {
  sections: [
    {
      heading: "Scope",
      paragraphs: [
        "These terms of use govern access to and use of the website bnmgroup.it, operated by BNM S.R.L. By using the website you accept these terms. If you do not accept them, please do not use the website.",
      ],
    },
    {
      heading: "Use of the website",
      paragraphs: [
        "You agree to use the website lawfully and not to interfere with its operation, security or availability, and not to attempt unauthorised access to any part of it.",
      ],
    },
    {
      heading: "No warranty",
      paragraphs: [
        "The website and its content are provided \"as is\" and \"as available\". BNM S.R.L. does not warrant that the website will be uninterrupted or error-free, or that the information it contains is complete, current or fit for a particular purpose.",
      ],
    },
    {
      heading: "Intellectual property",
      paragraphs: [
        "The content of this website is protected and may not be used except as permitted in the Legal Notice.",
      ],
    },
    {
      heading: "No offer",
      paragraphs: [
        "Nothing on this website constitutes a binding commercial offer. Any transaction is subject to a separate written agreement between the parties.",
      ],
    },
    {
      heading: "Changes",
      paragraphs: [
        "BNM S.R.L. may update these terms or the website content at any time. The version published on this page applies to your current use of the website.",
      ],
    },
    {
      heading: "Governing law",
      paragraphs: [
        "These terms are governed by Italian law. The competent court of the place where BNM S.R.L. has its registered office has jurisdiction, without prejudice to any mandatory protections that may apply.",
      ],
    },
  ],
};

const TERMS_IT: LegalPageContent = {
  sections: [
    {
      heading: "Ambito",
      paragraphs: [
        "I presenti termini d'uso regolano l'accesso e l'utilizzo del sito bnmgroup.it, gestito da BNM S.R.L. Utilizzando il sito accetti i presenti termini. Se non li accetti, ti preghiamo di non utilizzare il sito.",
      ],
    },
    {
      heading: "Utilizzo del sito",
      paragraphs: [
        "Ti impegni a utilizzare il sito in modo lecito, a non interferire con il suo funzionamento, la sua sicurezza o la sua disponibilità e a non tentare accessi non autorizzati ad alcuna sua parte.",
      ],
    },
    {
      heading: "Assenza di garanzie",
      paragraphs: [
        "Il sito e i suoi contenuti sono forniti \"così come sono\" e \"secondo disponibilità\". BNM S.R.L. non garantisce che il sito sia privo di interruzioni o errori, né che le informazioni in esso contenute siano complete, aggiornate o idonee a uno scopo specifico.",
      ],
    },
    {
      heading: "Proprietà intellettuale",
      paragraphs: [
        "I contenuti di questo sito sono protetti e non possono essere utilizzati se non nei limiti consentiti dalle Note Legali.",
      ],
    },
    {
      heading: "Nessuna offerta",
      paragraphs: [
        "Nessun elemento di questo sito costituisce un'offerta commerciale vincolante. Qualsiasi transazione è soggetta a un separato accordo scritto tra le parti.",
      ],
    },
    {
      heading: "Modifiche",
      paragraphs: [
        "BNM S.R.L. può aggiornare i presenti termini o i contenuti del sito in qualsiasi momento. La versione pubblicata in questa pagina si applica all'utilizzo corrente del sito.",
      ],
    },
    {
      heading: "Legge applicabile",
      paragraphs: [
        "I presenti termini sono regolati dalla legge italiana. È competente il foro del luogo in cui BNM S.R.L. ha la propria sede legale, fatte salve eventuali tutele inderogabili applicabili.",
      ],
    },
  ],
};

// ─── PRIVACY POLICY ────────────────────────────────────────────────
const PRIVACY_EN: LegalPageContent = {
  sections: [
    {
      heading: "Data controller",
      paragraphs: [
        "The data controller for personal data processed through this website is BNM S.R.L., with registered office at Via Antonio Vivaldi, 12, 56022 Castelfranco di Sotto (PI), Italy. For any privacy-related request you can contact us at info@bnmgroup.it or by certified email (PEC) at bnmsrl@arubapec.it.",
      ],
    },
    {
      heading: "What data we process",
      paragraphs: [
        "When you use the contact form we process the data you provide: name, company, business email, phone (optional), country (optional), area of interest (optional) and your message, together with your consent to be contacted.",
        "We also process limited technical metadata associated with the submission — such as IP address, approximate country, browser user-agent and timestamp — for security and to prevent abuse.",
      ],
    },
    {
      heading: "Purposes and legal basis",
      paragraphs: [
        "We process this data to respond to your inquiry and manage the resulting business communication (pre-contractual or contractual measures taken at your request, and our legitimate interest in handling commercial contacts), and to protect the security of the website (legitimate interest). Providing the requested fields is necessary to handle your inquiry; without them we may be unable to respond.",
      ],
    },
    {
      heading: "Storage and providers",
      paragraphs: [
        "Contact submissions are stored using Cloudflare infrastructure (Cloudflare KV). This website and its supporting infrastructure are provided by Cloudflare; certified email (PEC) is provided by Aruba. These providers process data within their respective roles; see the section below regarding processing outside the European Economic Area.",
      ],
    },
    {
      heading: "International transfers",
      paragraphs: [
        "Some providers (for example Cloudflare) may process personal data on servers located outside the European Economic Area, including in the United States. Such transfers take place on the basis of the European Commission's Standard Contractual Clauses and/or, where the provider is certified, the EU–US Data Privacy Framework.",
        "You may request a copy of the safeguards applied to these transfers by contacting us at info@bnmgroup.it or by certified email (PEC) at bnmsrl@arubapec.it.",
      ],
    },
    {
      heading: "Retention",
      paragraphs: [
        "We keep contact inquiries for up to 24 months, unless a longer period is required for legal, contractual or dispute-related purposes, after which the data is deleted or anonymised.",
      ],
    },
    {
      heading: "Your rights",
      paragraphs: [
        "Under the GDPR (Regulation (EU) 2016/679) you have the right to access, rectify, erase or restrict the processing of your personal data, to object to processing, and to data portability where applicable. You may exercise these rights by contacting us at the addresses above. You also have the right to lodge a complaint with the Italian Data Protection Authority (Garante per la protezione dei dati personali).",
      ],
    },
    {
      heading: "Cookies",
      paragraphs: [
        "Information about the cookies used by this website is provided in the Cookie Policy.",
      ],
    },
    {
      heading: "Changes",
      paragraphs: [
        "We may update this privacy policy from time to time. The version published on this page applies to your current use of the website.",
      ],
    },
  ],
};

const PRIVACY_IT: LegalPageContent = {
  sections: [
    {
      heading: "Titolare del trattamento",
      paragraphs: [
        "Il titolare del trattamento dei dati personali trattati tramite questo sito è BNM S.R.L., con sede legale in Via Antonio Vivaldi, 12, 56022 Castelfranco di Sotto (PI), Italia. Per qualsiasi richiesta in materia di privacy puoi contattarci all'indirizzo info@bnmgroup.it o tramite posta elettronica certificata (PEC) all'indirizzo bnmsrl@arubapec.it.",
      ],
    },
    {
      heading: "Quali dati trattiamo",
      paragraphs: [
        "Quando utilizzi il modulo di contatto trattiamo i dati che fornisci: nome, azienda, email aziendale, telefono (facoltativo), paese (facoltativo), area di interesse (facoltativa) e il tuo messaggio, unitamente al consenso a essere contattato.",
        "Trattiamo inoltre limitati metadati tecnici associati all'invio — quali indirizzo IP, paese approssimativo, user-agent del browser e data e ora — per finalità di sicurezza e prevenzione degli abusi.",
      ],
    },
    {
      heading: "Finalità e base giuridica",
      paragraphs: [
        "Trattiamo questi dati per rispondere alla tua richiesta e gestire la conseguente comunicazione commerciale (misure precontrattuali o contrattuali adottate su tua richiesta e nostro legittimo interesse alla gestione dei contatti commerciali) e per proteggere la sicurezza del sito (legittimo interesse). Il conferimento dei dati richiesti è necessario per gestire la tua richiesta; in loro assenza potremmo non essere in grado di risponderti.",
      ],
    },
    {
      heading: "Conservazione e fornitori",
      paragraphs: [
        "Gli invii dal modulo di contatto sono archiviati tramite l'infrastruttura Cloudflare (Cloudflare KV). Questo sito e la relativa infrastruttura sono forniti da Cloudflare; la posta elettronica certificata (PEC) è fornita da Aruba. Tali fornitori trattano i dati nell'ambito dei rispettivi ruoli; vedi la sezione seguente in merito al trattamento al di fuori dello Spazio Economico Europeo.",
      ],
    },
    {
      heading: "Trasferimenti internazionali",
      paragraphs: [
        "Alcuni fornitori (ad esempio Cloudflare) possono trattare dati personali su server situati al di fuori dello Spazio Economico Europeo, inclusi gli Stati Uniti. Tali trasferimenti avvengono sulla base delle Clausole Contrattuali Standard della Commissione Europea e/o, ove il fornitore sia certificato, del Data Privacy Framework UE–USA.",
        "Puoi richiedere una copia delle garanzie applicate a tali trasferimenti contattandoci all'indirizzo info@bnmgroup.it o tramite PEC all'indirizzo bnmsrl@arubapec.it.",
      ],
    },
    {
      heading: "Conservazione",
      paragraphs: [
        "Conserviamo le richieste di contatto per un massimo di 24 mesi, salvo che un periodo più lungo sia necessario per finalità legali, contrattuali o di contenzioso, trascorsi i quali i dati vengono cancellati o resi anonimi.",
      ],
    },
    {
      heading: "I tuoi diritti",
      paragraphs: [
        "Ai sensi del GDPR (Regolamento (UE) 2016/679) hai il diritto di accedere ai tuoi dati personali, di rettificarli, cancellarli o limitarne il trattamento, di opporti al trattamento e alla portabilità dei dati ove applicabile. Puoi esercitare tali diritti contattandoci agli indirizzi sopra indicati. Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali.",
      ],
    },
    {
      heading: "Cookie",
      paragraphs: [
        "Le informazioni sui cookie utilizzati da questo sito sono riportate nella Cookie Policy.",
      ],
    },
    {
      heading: "Modifiche",
      paragraphs: [
        "Potremmo aggiornare periodicamente la presente informativa sulla privacy. La versione pubblicata in questa pagina si applica all'utilizzo corrente del sito.",
      ],
    },
  ],
};

// ─── COOKIE POLICY ─────────────────────────────────────────────────
const COOKIE_EN: LegalPageContent = {
  sections: [
    {
      heading: "Overview",
      paragraphs: [
        "This website is designed to work without marketing or profiling cookies. We do not use Google Analytics, advertising trackers or marketing analytics.",
      ],
    },
    {
      heading: "Cookies used",
      paragraphs: [
        "Only strictly necessary technical or security cookies may be set, where required for the website to function or to protect it — for example, cookies that may be set by our infrastructure provider, Cloudflare, for security and network purposes. These cookies are not used for advertising or profiling.",
      ],
    },
    {
      heading: "Managing cookies",
      paragraphs: [
        "Because only strictly necessary cookies may be used, no consent banner is required. You can still control or delete cookies through your browser settings; disabling strictly necessary cookies may affect how the website works.",
      ],
    },
    {
      heading: "Changes",
      paragraphs: [
        "If we introduce additional cookies in the future, we will update this policy accordingly before doing so.",
      ],
    },
    {
      heading: "More information",
      paragraphs: [
        "For questions about cookies or your personal data, contact us at info@bnmgroup.it. See also our Privacy Policy.",
      ],
    },
  ],
};

const COOKIE_IT: LegalPageContent = {
  sections: [
    {
      heading: "Panoramica",
      paragraphs: [
        "Questo sito è progettato per funzionare senza cookie di marketing o di profilazione. Non utilizziamo Google Analytics, tracker pubblicitari o strumenti di analisi di marketing.",
      ],
    },
    {
      heading: "Cookie utilizzati",
      paragraphs: [
        "Possono essere impostati esclusivamente cookie tecnici o di sicurezza strettamente necessari, ove richiesti per il funzionamento del sito o per la sua protezione — ad esempio cookie eventualmente impostati dal nostro fornitore di infrastruttura, Cloudflare, per finalità di sicurezza e di rete. Tali cookie non sono utilizzati per finalità pubblicitarie o di profilazione.",
      ],
    },
    {
      heading: "Gestione dei cookie",
      paragraphs: [
        "Poiché possono essere utilizzati solo cookie strettamente necessari, non è richiesto alcun banner di consenso. Puoi comunque controllare o eliminare i cookie tramite le impostazioni del tuo browser; la disattivazione dei cookie strettamente necessari può influire sul funzionamento del sito.",
      ],
    },
    {
      heading: "Modifiche",
      paragraphs: [
        "Qualora in futuro introducessimo cookie aggiuntivi, aggiorneremo la presente informativa di conseguenza prima di farlo.",
      ],
    },
    {
      heading: "Ulteriori informazioni",
      paragraphs: [
        "Per domande sui cookie o sui tuoi dati personali, contattaci all'indirizzo info@bnmgroup.it. Consulta anche la nostra Informativa sulla privacy.",
      ],
    },
  ],
};

// ─── REGISTRY ──────────────────────────────────────────────────────
export const LEGAL_CONTENT: Partial<
  Record<RouteKey, { en: LegalPageContent; it: LegalPageContent }>
> = {
  "legal.privacy": { en: PRIVACY_EN, it: PRIVACY_IT },
  "legal.cookies": { en: COOKIE_EN, it: COOKIE_IT },
  "legal.notice": { en: NOTICE_EN, it: NOTICE_IT },
  "legal.accessibility": { en: ACCESS_EN, it: ACCESS_IT },
  "legal.terms": { en: TERMS_EN, it: TERMS_IT },
};
